import React, { useCallback, useRef, useState } from "react";

import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { useDidUpdate, useModals } from "hooks";
import { useReportsActions, useReportsQueriesSelector } from "store";
import { CanvasView, CustomConfig, DateRange, DateRangeValueType, GRID_CONSTANTS } from "store/types";

import {
  AICard,
  BackgroundDrawer,
  Canvas,
  ChartView,
  CustomizationModal,
  DataTable,
  KPICard,
  LoadTemplateDialog,
  LogoDrawer,
  SaveTemplateDialog,
  Sidebar,
  SummaryCard,
  TextCard,
  Toolbar
} from "./components";
import { ReportGeneratorProvider, useReportGeneratorContext } from "./context/ReportGenerator-context";
import { useCanvasDragDrop, usePdfExport, useTemplateHook } from "./hooks";

const { GAP, GRID_CELL_HEIGHT, GRID_COLUMNS } = GRID_CONSTANTS;

const ReportGeneratorContent: React.FC = () => {
  const { dateRange, currency, filters, exchangeMethod, use_accounting_date } = useReportsQueriesSelector();
  const { noticeModal, errorModal } = useModals();
  const { setQueriesAction, getBulkPieChartsAction, getCubicAction, getReportTableAction, getMeasureSummaryAction } =
    useReportsActions();

  const {
    availableViews,
    pages,
    currentPageIndex,
    loading,
    error,
    reportTitle,
    logoUrl,
    logoFile,
    backgroundUrl,
    backgroundFile,
    currentPageCards,
    gridCellWidth,
    draggedView,
    dragPreview,
    pageMeta,
    setPages,
    setPageMeta,
    setCurrentPageIndex,
    setError,
    setReportTitle,
    setLogoUrl,
    setLogoFile,
    setBackgroundUrl,
    setBackgroundFile,
    setDraggedView,
    setDragPreview,
    addPage,
    removePage,
    addCardToCanvas,
    removeCardFromCanvas,
    updateCardConfig,
    updateCardSpan,
    updateCardPosition,
    snapToGrid,
    getCardWidth,
    getCardHeight,
    isPositionOccupied,
    findAvailablePosition,
    clearCanvas
  } = useReportGeneratorContext();

  const canvasRef = useRef<HTMLDivElement>(null);

  // Customization modal state
  const [customizingCard, setCustomizingCard] = useState<CanvasView | null>(null);

  // Resize state
  const [resizingCard, setResizingCard] = useState<{
    id: string;
    startSpan: number;
    startRowSpan: number;
    startX: number;
    startY: number;
  } | null>(null);

  // Refetching cards loading state
  const [refetchingCardIds, setRefetchingCardIds] = useState<Set<string>>(new Set());

  // Drawer states
  const [logoDrawerOpen, setLogoDrawerOpen] = useState(false);
  const [backgroundDrawerOpen, setBackgroundDrawerOpen] = useState(false);

  // Template dialog states
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [loadTemplateDialogOpen, setLoadTemplateDialogOpen] = useState(false);

  // Template actions hook
  const {
    templates,
    loadingTemplates,
    savingTemplate,
    loadingTemplate,
    fetchTemplates,
    saveTemplate,
    updateTemplate,
    loadTemplate,
    deleteTemplate,
    currentTemplateId,
    setCurrentTemplateId
  } = useTemplateHook({
    pages,
    pageMeta,
    reportTitle,
    logoUrl,
    logoFile,
    backgroundFile,
    backgroundUrl,
    setPages,
    setPageMeta,
    setReportTitle,
    setLogoUrl,
    setBackgroundUrl,
    setCurrentPageIndex
  });

  // Get current template info for display
  const currentTemplate = currentTemplateId ? templates.find((t) => t.id === currentTemplateId) : undefined;
  const currentTemplateName = currentTemplate?.name;
  const currentTemplateDescription = currentTemplate?.description;

  // PDF Export hook
  const { exporting, handleExportPdf } = usePdfExport({
    canvasRef,
    pages,
    currentPageIndex,
    reportTitle,
    setCurrentPageIndex,
    setError
  });

  // Drag and drop hook
  const {
    draggingCard,
    cardDragPreview,
    handleViewDragStart,
    handleViewDragEnd,
    handleCanvasDragOver,
    handleCanvasDrop,
    handleCanvasDragLeave,
    handleQuickAddCard,
    handleCardDragStart,
    handleCardDragMove,
    handleCardDragEnd
  } = useCanvasDragDrop({
    canvasRef,
    currentPageCards,
    gridCellWidth,
    draggedView,
    dragPreview,
    setDraggedView,
    setDragPreview,
    snapToGrid,
    isPositionOccupied,
    findAvailablePosition,
    addCardToCanvas,
    updateCardPosition,
    setError
  });

  // Refetch all configured cards when currency or dateRange changes
  useDidUpdate(() => {
    const refetchAllCards = async () => {
      const cardsToRefetch: Array<{ pageIdx: number; card: CanvasView }> = [];
      pages.forEach((page, pageIdx) => {
        page.forEach((card) => {
          if (card.customConfig && Object.keys(card.customConfig).length > 0) {
            if (["chart", "kpi_card", "summary_card", "data_table"].includes(card.type)) {
              cardsToRefetch.push({ pageIdx, card });
            }
          }
        });
      });

      if (cardsToRefetch.length === 0) return;

      // Mark all cards as loading
      setRefetchingCardIds(new Set(cardsToRefetch.map(({ card }) => card.id)));

      const results = await Promise.allSettled(
        cardsToRefetch.map(async ({ card }) => {
          const config = card.customConfig!;
          let chartData: CanvasView["data"];

          if (card.type === "chart" && config.graphDataFormation) {
            const result = await getBulkPieChartsAction({
              filters,
              graphDataFormation: config.graphDataFormation,
              dateRange,
              currency,
              exchangeMethod,
              use_accounting_date
            });
            const chartKey = Object.keys(config.graphDataFormation)[0];
            if (result && result[chartKey]) {
              chartData = result[chartKey];
            }
          } else if (card.type === "kpi_card" && config.cubics) {
            const kpiData = await getCubicAction({
              cubics: config.cubics,
              pageType: "document",
              filters,
              dateRange,
              currency,
              exchangeMethod,
              use_accounting_date
            });
            if (kpiData) {
              chartData = kpiData;
            }
          } else if (card.type === "summary_card" && config.parameter) {
            const summaryData = await getMeasureSummaryAction({
              parameter: config.parameter,
              filters,
              dateRange,
              currency,
              exchangeMethod,
              use_accounting_date
            });
            if (summaryData) {
              chartData = {
                parameter: summaryData.parameter,
                total: summaryData.total,
                average: summaryData.average,
                min: summaryData.min,
                max: summaryData.max,
                measureName: config.parameter
              };
            }
          } else if (card.type === "data_table" && config.columns?.length) {
            const tableData = await getReportTableAction({
              dateRange,
              currency,
              exchangeMethod,
              filters,
              measures: config.measures || [],
              calculatedFields: config.calculatedFields || [],
              characteristics: config.characteristics || [],
              columns: config.columns,
              groupBy: config.groupBy || [],
              columnBy: config.columnBy || "measure",
              rowBy: config.rowBy || "deal",
              originalCurrency: false,
              use_accounting_date,
              disableDispatch: true,
              indexName: config.indexName
            });
            if (tableData?.tableData) {
              const apiColumns = tableData.tableData.columns || [];
              chartData = {
                columns: [
                  { key: "name", title: "Name" },
                  ...apiColumns.map((col: { key: string; title: string }) => ({ key: col.key, title: col.title }))
                ],
                data: (tableData.tableData.rows || []) as Array<{
                  title?: string;
                  valueData?: Record<string, number | null>;
                }>
              };
            }
          }

          return { cardId: card.id, chartData };
        })
      );

      setPages((prev) => {
        const updated = prev.map((page) => [...page]);
        results.forEach((result, index) => {
          if (result.status === "fulfilled" && result.value.chartData !== undefined) {
            const { cardId, chartData } = result.value;
            const { pageIdx } = cardsToRefetch[index];
            updated[pageIdx] = updated[pageIdx].map((c) => (c.id === cardId ? { ...c, data: chartData } : c));
          }
        });
        return updated;
      });

      // Clear loading state
      setRefetchingCardIds(new Set());
    };

    refetchAllCards();
  }, [currency, dateRange]);

  // Date range handler
  const isInTheFuture = (date: string): boolean => {
    const today = new Date();
    today.setHours(23, 59, 59, 998);
    return new Date(date) > today;
  };

  const handleDateRangeChange = useCallback(
    (data: DateRange & { value: DateRangeValueType }, use_accounting_date?: boolean) => {
      const { to, from } = data;
      use_accounting_date = use_accounting_date || false;
      if ((to && isInTheFuture(to)) || (from && isInTheFuture(from))) {
        noticeModal({
          msg: "Notice - Report can not show future values of different currency. Different currency's in future dates are ignored."
        });
      }

      if (to && from && from > to) {
        return errorModal({ msg: "'Date From' must be less than 'Date To'" });
      }

      setQueriesAction({
        dateRange: data,
        use_accounting_date
      });
    },
    [noticeModal, errorModal, setQueriesAction]
  );

  // Auto-clean layout handler
  const handleAutoCleanLayout = useCallback(() => {
    if (currentPageCards.length === 0) return;

    const sortedCards = [...currentPageCards].sort((a, b) => {
      if (a.gridRow !== b.gridRow) return a.gridRow - b.gridRow;
      return a.gridCol - b.gridCol;
    });

    const maxRows = 20;
    const grid: boolean[][] = Array(maxRows)
      .fill(null)
      .map(() => Array(GRID_COLUMNS).fill(false));

    const isPositionAvailable = (col: number, row: number, span: number, rowSpan: number): boolean => {
      if (col < 0 || col + span > GRID_COLUMNS) return false;
      if (row < 0 || row + rowSpan > maxRows) return false;

      for (let r = row; r < row + rowSpan; r++) {
        for (let c = col; c < col + span; c++) {
          if (grid[r][c]) return false;
        }
      }
      return true;
    };

    const markOccupied = (col: number, row: number, span: number, rowSpan: number) => {
      for (let r = row; r < row + rowSpan; r++) {
        for (let c = col; c < col + span; c++) {
          grid[r][c] = true;
        }
      }
    };

    const findFirstAvailablePosition = (span: number, rowSpan: number): { col: number; row: number } | null => {
      for (let row = 0; row < maxRows - rowSpan + 1; row++) {
        for (let col = 0; col <= GRID_COLUMNS - span; col++) {
          if (isPositionAvailable(col, row, span, rowSpan)) {
            return { col, row };
          }
        }
      }
      return null;
    };

    const newCards: CanvasView[] = sortedCards.map((card) => {
      const span = card.gridSpan || 6;
      const rowSpan = card.gridRowSpan || 2;

      const position = findFirstAvailablePosition(span, rowSpan);
      if (position) {
        markOccupied(position.col, position.row, span, rowSpan);
        return { ...card, gridCol: position.col, gridRow: position.row };
      }
      return card;
    });

    setPages((prev) => {
      const updated = [...prev];
      updated[currentPageIndex] = newCards;
      return updated;
    });
  }, [currentPageCards, currentPageIndex, setPages]);

  // Resize handlers
  const handleResizeStart = useCallback((card: CanvasView, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingCard({
      id: card.id,
      startSpan: card.gridSpan,
      startRowSpan: card.gridRowSpan || 2,
      startX: e.clientX,
      startY: e.clientY
    });
  }, []);

  const handleResizeMove = useCallback(
    (e: React.MouseEvent) => {
      if (!resizingCard || !canvasRef.current) return;

      const deltaX = e.clientX - resizingCard.startX;
      const deltaY = e.clientY - resizingCard.startY;

      const spanDelta = Math.round(deltaX / (gridCellWidth + GAP));
      const rowSpanDelta = Math.round(deltaY / (GRID_CELL_HEIGHT + GAP));

      const newSpan = Math.max(3, Math.min(12, resizingCard.startSpan + spanDelta));
      const newRowSpan = Math.max(1, Math.min(8, resizingCard.startRowSpan + rowSpanDelta));

      const card = currentPageCards.find((c) => c.id === resizingCard.id);
      if (card && !isPositionOccupied(card.gridCol, card.gridRow, newSpan, card.id)) {
        updateCardSpan(resizingCard.id, newSpan, newRowSpan);
      }
    },
    [resizingCard, gridCellWidth, currentPageCards, isPositionOccupied, updateCardSpan]
  );

  const handleResizeEnd = useCallback(() => {
    setResizingCard(null);
  }, []);

  // Customization handlers
  const handleOpenCustomization = useCallback((card: CanvasView) => {
    setCustomizingCard(card);
  }, []);

  const handleCloseCustomization = useCallback(() => {
    setCustomizingCard(null);
  }, []);

  const handleApplyCustomization = useCallback(
    (config: CustomConfig) => {
      if (customizingCard) {
        updateCardConfig(customizingCard.id, config);
      }
    },
    [customizingCard, updateCardConfig]
  );

  // Background handlers
  const handleApplyBackground = useCallback(
    (url: string, opacity: number, file: File | null) => {
      setBackgroundUrl(url || null);
      setPageMeta((prev) => ({
        ...prev,
        backgroundOpacity: opacity
      }));
      setBackgroundFile(file);
      setBackgroundDrawerOpen(false);
    },
    [setBackgroundUrl, setPageMeta, setBackgroundFile]
  );

  const handleRemoveBackground = useCallback(() => {
    setBackgroundUrl(null);
    setPageMeta({ backgroundOpacity: 100 });
    setBackgroundFile(null);
  }, [setBackgroundUrl, setPageMeta, setBackgroundFile]);

  // Logo handlers
  const handleApplyLogo = useCallback(
    (url: string | null, file: File | null) => {
      setLogoUrl(url);
      setLogoFile(file);
      setLogoDrawerOpen(false);
    },
    [setLogoUrl, setLogoFile]
  );

  const handleRemoveLogo = useCallback(() => {
    setLogoUrl(null);
    setLogoFile(null);
  }, [setLogoUrl, setLogoFile]);

  // New report handler - clears template and resets canvas
  const handleNewReport = useCallback(() => {
    clearCanvas();
    setCurrentTemplateId(null);
    setReportTitle("Altius Report");
    setLogoUrl(null);
    setLogoFile(null);
    setBackgroundUrl(null);
    setBackgroundFile(null);
  }, [clearCanvas, setCurrentTemplateId, setReportTitle, setLogoUrl, setLogoFile, setBackgroundUrl, setBackgroundFile]);

  // Render card content based on type
  const renderCardContent = useCallback(
    (card: CanvasView) => {
      const isRefetching = refetchingCardIds.has(card.id);
      const graphData = card.customConfig?.graphDataFormation;
      const groupBy = graphData ? Object.values(graphData)[0]?.groupBy || "deal" : "deal";
      const cardWidth = getCardWidth(card.gridSpan);
      const cardHeight = getCardHeight(card.gridRowSpan || 2);

      let content: React.ReactNode;

      switch (card.type) {
        case "chart":
          content = <ChartView data={card.data} groupBy={groupBy} width={cardWidth} height={cardHeight} />;
          break;
        case "data_table":
          content = <DataTable data={card.data} title={card.title} />;
          break;
        case "summary_card":
          content = <SummaryCard data={card.data} title={card.title} />;
          break;
        case "kpi_card":
          content = <KPICard data={card.data} title={card.title} />;
          break;
        case "text_card":
          content = (
            <TextCard
              data={card.customConfig?.content ? { content: card.customConfig.content } : card.data}
              title={card.title}
            />
          );
          break;
        case "ai_card":
          content = (
            <AICard data={card.data} question={card.customConfig?.question} answer={card.customConfig?.answer} />
          );
          break;
        default:
          content = (
            <Box sx={{ p: 1, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "0.75rem", color: "#64748b" }}>{card.title}</span>
            </Box>
          );
      }

      if (isRefetching) {
        return (
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            {content}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                borderRadius: "inherit"
              }}
            >
              <CircularProgress size={32} />
            </Box>
          </Box>
        );
      }

      return content;
    },
    [getCardWidth, getCardHeight, refetchingCardIds]
  );

  return (
    <Stack flexDirection="row" sx={{ height: "100%", overflow: "hidden", gap: "20px" }}>
      {/* Left sidebar */}
      <Sidebar
        availableViews={availableViews}
        logoUrl={logoUrl}
        onViewDragStart={handleViewDragStart}
        onViewDragEnd={handleViewDragEnd}
        onQuickAddCard={handleQuickAddCard}
        onOpenLogoDrawer={() => setLogoDrawerOpen(true)}
        onOpenBackgroundDrawer={() => setBackgroundDrawerOpen(true)}
        onAutoCleanLayout={handleAutoCleanLayout}
      />
      {/* Main content area */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", gap: "20px" }}>
        {/* Toolbar */}
        <Toolbar
          reportTitle={reportTitle}
          dateRange={dateRange}
          currentPageIndex={currentPageIndex}
          pagesCount={pages.length}
          // loading={loading}
          // exporting={exporting}
          hasContent={pages.some((page) => page.length > 0)}
          savingTemplate={savingTemplate}
          currentTemplateName={currentTemplateName}
          onTitleChange={setReportTitle}
          onDateRangeChange={handleDateRangeChange}
          onPrevPage={() => setCurrentPageIndex(currentPageIndex - 1)}
          onNextPage={() => setCurrentPageIndex(currentPageIndex + 1)}
          onAddPage={addPage}
          onRemovePage={() => removePage(currentPageIndex)}
          // onExportPdf={handleExportPdf}
          onSaveTemplate={() => setSaveTemplateDialogOpen(true)}
          onLoadTemplate={() => setLoadTemplateDialogOpen(true)}
        />
        {/* Error display */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {/* Canvas area */}
        <Canvas
          canvasRef={canvasRef}
          loading={loading}
          currentPageCards={currentPageCards}
          currentPageMeta={pageMeta}
          backgroundUrl={backgroundUrl}
          logoUrl={logoUrl}
          reportTitle={reportTitle}
          gridCellWidth={gridCellWidth}
          dragPreview={dragPreview}
          cardDragPreview={cardDragPreview}
          draggingCardId={draggingCard?.id || null}
          resizingCardId={resizingCard?.id || null}
          onCanvasDragOver={handleCanvasDragOver}
          onCanvasDrop={handleCanvasDrop}
          onCanvasDragLeave={handleCanvasDragLeave}
          onCardDragStart={handleCardDragStart}
          onCardDragMove={handleCardDragMove}
          onCardDragEnd={handleCardDragEnd}
          onResizeStart={handleResizeStart}
          onResizeMove={handleResizeMove}
          onResizeEnd={handleResizeEnd}
          onOpenCustomization={handleOpenCustomization}
          onRemoveCard={removeCardFromCanvas}
          onNewReport={handleNewReport}
          onAddPage={addPage}
          onRemovePage={() => removePage(currentPageIndex)}
          canRemovePage={pages.length > 1}
          onExportPdf={handleExportPdf}
          showNewReportAction={Boolean(currentTemplateId)}
          hasContent={pages.some((page) => !!page.length)}
          exporting={exporting}
          getCardWidth={getCardWidth}
          getCardHeight={getCardHeight}
          renderCardContent={renderCardContent}
        />
      </Box>
      {/* Customization Modal */}
      <CustomizationModal
        open={Boolean(customizingCard)}
        onClose={handleCloseCustomization}
        onApply={handleApplyCustomization}
        view={customizingCard}
      />
      {/* Background Drawer */}
      <BackgroundDrawer
        open={backgroundDrawerOpen}
        currentBackgroundUrl={backgroundUrl}
        currentOpacity={pageMeta.backgroundOpacity ?? 100}
        onClose={() => setBackgroundDrawerOpen(false)}
        onApply={handleApplyBackground}
        onRemove={handleRemoveBackground}
      />
      {/* Logo Drawer */}
      <LogoDrawer
        open={logoDrawerOpen}
        currentLogoUrl={logoUrl}
        onClose={() => setLogoDrawerOpen(false)}
        onApply={handleApplyLogo}
        onRemove={handleRemoveLogo}
      />
      {/* Save Template Dialog */}
      <SaveTemplateDialog
        open={saveTemplateDialogOpen}
        onClose={() => setSaveTemplateDialogOpen(false)}
        onSave={saveTemplate}
        onUpdate={updateTemplate}
        saving={savingTemplate}
        currentTemplateId={currentTemplateId}
        currentTemplateName={currentTemplateName}
        currentTemplateDescription={currentTemplateDescription}
      />
      {/* Load Template Dialog */}
      <LoadTemplateDialog
        open={loadTemplateDialogOpen}
        onClose={() => setLoadTemplateDialogOpen(false)}
        templates={templates}
        loading={loadingTemplates}
        loadingTemplate={loadingTemplate}
        onFetchTemplates={fetchTemplates}
        onLoadTemplate={loadTemplate}
        onDeleteTemplate={deleteTemplate}
        currentTemplateId={currentTemplateId}
      />
    </Stack>
  );
};

export const ReportGenerator: React.FC = () => {
  return (
    <ReportGeneratorProvider>
      <ReportGeneratorContent />
    </ReportGeneratorProvider>
  );
};
